<template>
  <v-container fluid>
    <v-row>
      <v-col cols="4">
        <v-card-title class="pt-0 pl-0">
          Jackpots
        </v-card-title>
      </v-col>
    </v-row>
    <v-row class="align-center">
      <v-col cols="3">
        <select-project
          v-model="searchArgs.project"
          label="Select project"
          @input="onProjectInput"
        />
      </v-col>
      <v-col
        cols="2"
        class="d-flex"
      >
        <v-btn
          v-if="canEdit"
          color="primary"
          :loading="isLoading"
          @click="onAddNewJackpot()"
        >
          Add New Jackpot
        </v-btn>
        <template
          v-if="canEdit && !!selectedJackpots.length"
        >
          <v-tooltip
            top
          >
            <template #activator="{ on }">
              <v-btn
                class="ml-2"
                color="warning"
                icon
                v-on="on"
                @click="onStatusToggle('disabled')"
              >
                <v-icon>
                  mdi-close-box-outline
                </v-icon>
              </v-btn>
            </template>
            <span>Disable Selected</span>
          </v-tooltip>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                icon
                color="primary"
                v-on="on"
                @click="onStatusToggle('active')"
              >
                <v-icon>
                  mdi-play-circle
                </v-icon>
              </v-btn>
            </template>
            <span>Activate Selected</span>
          </v-tooltip>
        </template>
      </v-col>
      <v-col
        v-if="jackpots.length"
        cols="2"
      >
        <export
          class="justify-end"
          :report-name="`jackpots-${searchArgs.project}-${Date.now()}`"
          :data="jackpots"
          :fields="Object.keys(jackpots[0])"
          without_sg
        />
      </v-col>

      <v-col
        cols="5"
        class="ml-auto text-right d-flex"
        style="gap: 1rem;"
      >
        <div
          class="input-with-filters align-self-end"
          style="position: relative; width: 100%;"
        >
          <v-text-field
            v-model="searchArgs.filters.name"
            class="search-input"
            solo
            outlined
            flat
            dense
            :loading="isLoading"
            append-icon="mdi-tune-vertical"
            prepend-inner-icon="search"
            label="Search"
            hide-details
          >
            <template #append>
              <v-btn
                icon
                color="primary"
                @click="searchState.isFilterDisplayed = !searchState.isFilterDisplayed"
              >
                <v-icon>
                  mdi-tune-vertical
                </v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <v-card
            v-if="searchState.isFilterDisplayed"
            ref="filterCard"
            class="filter-card"
            :loading="isLoading"
          >
            <v-card-title
              v-text="'Filters'"
            />
            <v-divider />

            <v-row
              class="mx-0"
            >
              <v-col cols="4">
                <select-project
                  v-model="searchArgs.project"
                  label="Select Project"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="searchArgs.filters.status"
                  :disabled="isLoading"
                  :items="STATUS_OPTIONS"
                  label="Status"
                  multiple
                  attach
                  clearable
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row
              class="mx-0"
            >
              <v-col cols="4">
                <v-menu
                  v-model="searchState.menuFrom"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="searchArgs.from"
                      label="Date From"
                      prepend-icon="event"
                      placeholder="YYYY-MM-DD"
                      hide-details
                      v-on="on"
                      style="min-width: 140px"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="searchArgs.from" no-title scrollable @input="searchState.menuFrom = false"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="4">
                <v-menu
                  v-model="searchState.menuTo"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="searchArgs.to"
                      label="Date To"
                      prepend-icon="event"
                      placeholder="YYYY-MM-DD"
                      hide-details
                      v-on="on"
                      style="min-width: 140px"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="searchArgs.to" no-title scrollable @input="searchState.menuTo = false"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-card-actions
              class="justify-end"
            >
              <v-btn
                v-text="'Cancel'"
                color="error"
                text
                :loading="isLoading"
                @click="searchState.isFilterDisplayed = false"
              />
              <v-btn
                v-text="'Search'"
                color="primary"
                text
                :loading="isLoading"
                @click="onSearch"
              />
            </v-card-actions>
          </v-card>
        </div>
        <v-btn
          v-text="'Search'"
          color="primary"
          class="mt-3 mr-3"
          :disabled="isLoading"
          :loading="isLoading"
          @click="onSearch"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="pt-0">
        <v-card>
          <v-data-table
            v-model="selectedJackpots"
            :headers="headers"
            :items="sortedJackpots"
            :loading="isLoading"
            :options.sync="pagination"
            :footer-props="{ 'items-per-page-options': [ 50, 100, 200 ] }"
            :server-items-length="total"
            item-key="jackpot_id"
            :show-select="canEdit"
          >
            <template #item.entities="{ value }">
              {{ value.map(id => projectNameById[id] || 'N/A').join(', ') }}
            </template>
            <template #item.prizepool.amount_in_eur="{ item }">
              {{ item.prizepool.amount_in_eur }} / {{ item.rules.prizepool[0].amount }}
            </template>
            <template #item.status="{ value }">
              <v-chip
                outlined
                :color="COLOR_BY_STATUS[value]"
              >
                {{ statusLabelByValue[value] }}
              </v-chip>
            </template>
            <template #item.action="{ item }">
              <v-menu>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    v-on="on"
                  >
                    <v-icon>
                      mdi-tune
                    </v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    @click="editingTarget = item"
                  >
                    <v-list-item-icon>
                      <v-icon>
                        {{ canEdit ? 'mdi-pencil' : 'mdi-eye-outline' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ canEdit ? 'Edit' : 'Overview' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    @click="onLoadHistory(item)"
                  >
                    <v-list-item-icon>
                      <v-icon>
                        mdi-history
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        History
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-if="canEdit"
                    @click="onDuplicate(item)"
                  >
                    <v-list-item-icon>
                      <v-icon>
                        mdi-content-duplicate
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        Duplicate
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="onLoadAuditLog(item)">
                    <v-list-item-icon>
                      <v-icon>mdi-state-machine</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        Audit Log
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-if="canEdit && item.status !== 'active'"
                    @click="emitToggle({ jackpot_ids: [ item.jackpot_id, ], status: 'active' })"
                  >
                    <v-list-item-icon>
                      <v-icon>
                        mdi-play-circle
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        Activate
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-if="canEdit && item.status !== 'disabled'"
                    @click="emitToggle({ jackpot_ids: [ item.jackpot_id, ], status: 'disabled', })"
                  >
                    <v-list-item-icon>
                      <v-icon>
                        mdi-close-box-outline
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        Deactivate
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <drawer-dialog
      v-model="isEditing"
      width="70%"
      max-width="1200px"
      persistent
      no-click-animation
      @click:outside="jackpotFormConfirmClose"
      @keydown.esc="jackpotFormConfirmClose"
    >
      <jackpot-form
        v-if="isEditing"
        :jackpot="editingTarget"
        :currencies="currencies"
        :games="games"
        :game-name-by-id="gameNameById"
        :brand-name-by-id="brandNameById"
        :brands="brands"
        :is-loading="isLoading"
        :readonly="!canEdit"
        ref="jackpot-form"
        @close="jackpotFormConfirmClose"
        @save="$emit('save', { jackpot: $event, searchArgs: {...searchArgs, options: fmtPagination() } })"
      />
    </drawer-dialog>

    <drawer-dialog
      v-model="isHistoryDisplayed"
      width="70%"
      max-width="1200px"
    >
      <jackpot-history-form
        v-if="isHistoryDisplayed"
        :history="history"
        :is-loading="isLoading"
        :jackpot="historyTarget"
        :can-edit="canEdit"
        @show-history="onLoadHistory(historyTarget, $event)"
        @close="isHistoryDisplayed = false"
        @re-issue="$emit('re-issue', $event)"
      />
    </drawer-dialog>

    <drawer-dialog
      v-model="isAuditLogDisplayed"
      width="70%"
      max-width="1200px"
    >
      <jackpot-audit-log-form
        v-if="isAuditLogDisplayed"
        :audit-log="auditLog"
        :jackpot="auditLogTarget"
        :is-loading="isLoading"
        :project-name-by-id="projectNameById"
        :game-name-by-id="gameNameById"
        :brand-name-by-id="brandNameById"
        :currencies="currencies"
        :brands="brands"
        @close="isAuditLogDisplayed = false"
      />
    </drawer-dialog>
  </v-container>
</template>

<script>
import { get } from 'lodash'
import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
import { DrawerDialog } from '@/components/core/DrawerDialog'
import { COLOR_BY_STATUS, getEmptyJackpot, STATUS_OPTIONS } from '../consts'
import JackpotForm from './JackpotForm.vue'
import JackpotHistoryForm from './JackpotHistoryForm.vue'
import JackpotAuditLogForm from './JackpotAuditLogForm.vue'

export default {
  props: {
    isLoading: Boolean,
    jackpots: {
      type: Array,
      required: true,
    },
    games: {
      type: Array,
      required: true,
    },
    brands: {
      type: Array,
      required: true,
    },
    currencies: {
      type: Array,
      required: true,
    },
    projectNameById: {
      type: Object,
      required: true,
    },
    gameNameById: {
      type: Object,
      required: true,
    },
    brandNameById: {
      type: Object,
      required: true,
    },
    history: {
      type: Array,
      required: true,
    },
    auditLog: {
      type: Array,
      required: true,
    },
    lastProject: Number,
    saveSuccessFlag: Boolean,
    total: Number,
    canEdit: Boolean,
  },
  components: {
    SelectProject,
    DrawerDialog,
    JackpotForm,
    JackpotHistoryForm,
    JackpotAuditLogForm,
    Export: () => import('@/components/helper/Export'),
  },
  emits: ['save', 'search', 'toggle', 're-issue'],
  data() {
    return {
      searchArgs: {
        from: null,
        to: null,
        filters: {
          name: '',
          status: [ 'active', 'upcoming' ],
        },
        project: null,
      },
      searchState: {
        menuFrom: false,
        menuTo: false,
        isFilterDisplayed: false,
      },
      pagination: {
        itemsPerPage: 50,
        page: 1,
      },
      headers: [
        { text: 'Jackpot Name', value: 'name', },
        { text: 'Projects', value: 'entities', },
        { text: 'Start Date', value: 'start', },
        { text: 'End Date', value: 'end', },
        { text: 'Prize Pool', value: 'prizepool.amount_in_eur', },
        { text: 'Min Bet', value: 'min_bet_eur', },
        { text: 'Bet Sum', value: 'prizepool.bet_sum_eur', },
        { text: 'JP Wins', value: 'prizepool_wins', },
        { text: 'Rounds/Count', value: 'prizepool.rounds_count', },
        { text: 'Status', value: 'status', },
        { text: '', value: 'action', sortable: false },
      ],
      editingTarget: null,
      selectedJackpots: [],
      STATUS_OPTIONS,
      statusLabelByValue: Object.fromEntries(STATUS_OPTIONS.map(status => ([status.value, status.text]))),
      COLOR_BY_STATUS,
      historyTarget: null,
      auditLogTarget: null,
    }
  },

  computed: {
    isEditing: {
      get() {
        return !!this.editingTarget
      },
      set(value) {
        if (!value) {
          this.editingTarget = null
        }
      }
    },
    isHistoryDisplayed: {
      get() {
        return !!this.historyTarget
      },
      set(value) {
        if (!value) {
          this.historyTarget = null
        }
      }
    },
    isAuditLogDisplayed: {
      get() {
        return !!this.auditLogTarget
      },
      set(value) {
        if (!value) {
          this.auditLogTarget = null
        }
      }
    },

    // v-data-table with server-length prop expects the server to sort the results thus disabling the default sorting
    // We don't make a request on sort prop change so the results must be sorted manually
    sortedJackpots() {
      const jackpots = [...this.jackpots]

      const sortBy = this.pagination.sortBy?.[0]
      const sortDesc = this.pagination.sortDesc?.[0]

      if (!sortBy) return jackpots

      return jackpots.sort((a, b) => {
        let result

        if (typeof get(a, sortBy) === 'string') {
          result = get(a, sortBy).localeCompare(get(b, sortBy))
        } else {
          result = (get(a, sortBy) ?? 0) - (get(b, sortBy) ?? 0)
        }

        return sortDesc ? -result : result
      })
    },
  },

  methods: {
    onProjectInput(val) {
      if (val) {
        this.onSearch()
      }
    },

    onSearch() {
      this.$emit('search', {
        ...this.searchArgs,
        options: this.fmtPagination(),
      })
    },

    onAddNewJackpot() {
      const project = this.lastProject ? [this.lastProject] : []
      this.editingTarget = getEmptyJackpot(project)
    },

    fmtPagination() {
      return {
        items_per_page: this.pagination.itemsPerPage,
        page: this.pagination.page,
      }
    },

    onStatusToggle(status) {
      this.emitToggle({
        status,
        jackpot_ids: this.selectedJackpots.map(jackpot => jackpot.jackpot_id),
      })
    },

    onDuplicate(item) {
      const { jackpot_id, ...rest } = item
      this.editingTarget = rest
    },

    onLoadHistory(item, args = {}) {
      this.historyTarget = item

      this.$emit('show-history', {
        jackpot_id: item.jackpot_id,
        entity_id: this.searchArgs.project,
        ...args,
      })
    },

    onLoadAuditLog(item) {
      this.auditLogTarget = item

      this.$emit('show-audit-log', {
        jackpot_id: item.jackpot_id,
        entity_id: this.searchArgs.project,
      })
    },

    emitToggle(args) {
      this.$emit('toggle', {
        entity_id: this.searchArgs.project,
        ...args,
      })
    },

    jackpotFormConfirmClose() {
      const isPristine = this.$refs['jackpot-form'].isPristine
      if (isPristine) {
        this.isEditing = false
        return
      }

      return this.$root.$confirm('Confirm', `Close the form? All unsaved changes will be lost`)
        .then(yes => {
          if (yes) {
            this.isEditing = false
          }
        })
    },
  },

  watch: {
    isLoading(yes) {
      if (!yes) {
        this.searchState.isFilterDisplayed = false
        this.selectedJackpots = []
      }
    },
    saveSuccessFlag() {
      this.isEditing = false
    },
    pagination: {
      deep: true,
      handler(after, before) {
        if (
          after.itemsPerPage !== before.itemsPerPage ||
          after.page !== before.page
        ) {
          this.onSearch()
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.input-with-filters {
  width: 100%;
  position: relative;

  .search-input {
    & ::v-deep .v-input__append-inner {
      margin-top: 0;
      align-self: center;
    }
  }
  .filter-card {
    position: absolute;
    top: calc(100% + 0.5rem);
    right: 0;
    width: 150%;
    max-width: unset;
    z-index: 5;
  }
}
</style>
